import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export type BillingInterval = "one_time" | "month" | "year";

export interface CartItem {
  id: string;
  title: string;
  src: string;
  thumbnail?: string;
  description?: string;
  priceLabel?: string;
  price?: number;
  currency?: string;
  isFree?: boolean;
  categoryName?: string;
  billingInterval?: BillingInterval;
  /** When 'unit', server overrides tier with the universal Single Video price. */
  purchaseMode?: "unit" | "tier";
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (videoId: string) => void;
  clearCart: () => void;
  isInCart: (videoId: string) => boolean;
  cartTotal: number;
  cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "iamerica-video-cart";

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = useCallback((item: CartItem) => {
    setCartItems((prev) => {
      if (prev.some((i) => i.id === item.id)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeFromCart = useCallback((videoId: string) => {
    setCartItems((prev) => prev.filter((i) => i.id !== videoId));
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const isInCart = useCallback(
    (videoId: string) => cartItems.some((i) => i.id === videoId),
    [cartItems]
  );

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price || 0), 0);
  const cartCount = cartItems.length;

  return (
    <CartContext.Provider
      value={{ cartItems, addToCart, removeFromCart, clearCart, isInCart, cartTotal, cartCount }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
};
